import React, { useState, useEffect } from 'react';
import './PresaleBox.css';
import { vendorAddress } from '../../utils/contractAddress';
import { parseEther, formatEther } from 'ethers';
import Modal from '../Modal';
import { useWeb3ModalAccount } from '@web3modal/ethers/react';

// Replace these with your actual image imports/paths:
//import ethIcon from '../assets/eth.png';
import bnbIcon from '../../assets/bnb-logo.png';
import usdtIcon from '../../assets/usdt-logo.svg';
//import usdcIcon from '../assets/usdc.png';

const PresaleBox = ({ countdownTimestamp, totalAmount, soldAmount, usdPrice, bnbPrice, usdtContract, vendorContract}) => {

  const {address, isConnected} = useWeb3ModalAccount();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState({
    title: '',
    message: '',
    txHash: '',
    error: '',
    isLoading: false,
  });

  const closeModal = () => {
    setModalOpen(false);
    setModalProps({
      title: '',
      message: '',
      txHash: '',
      error: '',
      isLoading: false,
    });
  };

  // Countdown Calculation (countdownTimestamp is assumed to be in UNIX seconds)
  const calculateTimeLeft = () => {
    const difference = countdownTimestamp * 1000 - new Date().getTime();
    let timeLeft = {};
    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }
    return timeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [countdownTimestamp]);

  // Progress Bar: calculate sold percentage
  const soldPercentage = totalAmount ? Math.min(100, (soldAmount / totalAmount) * 100) : 0;

  // Input Fields for amount to buy and amount to receive
  const [buyAmount, setBuyAmount] = useState('');
  const [receiveAmount, setReceiveAmount] = useState('');

  // For example, assume that the number entered in "buyAmount" is in USD and tokenPrice is USD per token
  // Then the number of tokens you'll receive is buyAmount / tokenPrice
  useEffect(() => {
    if (buyAmount && usdPrice && bnbPrice) {
      let received = 0;
      if(selectedPayment === 'BNB') {
        received = parseFloat(buyAmount) / parseFloat(bnbPrice);
      } else if(selectedPayment === 'USDT') {
        received = parseFloat(buyAmount) / parseFloat(usdPrice);
    }

      setReceiveAmount(received ? received.toFixed(2) : '');
    } else {
      setReceiveAmount('');
    }
  }, [buyAmount, usdPrice]);

  // Payment Options (clickable icons)
  const paymentOptions = [
    { name: 'BNB', icon: bnbIcon },
    { name: 'USDT', icon: usdtIcon },
  ];
  const [selectedPayment, setSelectedPayment] = useState(paymentOptions[0].name);


  const buyWithB = async () => {
    try {
      setModalOpen(true);
      setModalProps({
        title: 'Purchasing...',
        message: 'Please wait while your purchase transaction is processed.',
        isLoading: true,
        txHash: '',
        error: ''
      });
  
      const amount = parseEther(buyAmount);
      // Call the buyWithBNB function and send BNB (native coin)
      const tx = await vendorContract.buyWithBNB({ value: amount });
      const receipt = await tx.wait();

      setModalProps({
        title: 'Transaction Successful',
        message: 'Your transaction has been completed successfully.',
        isLoading: false,
        txHash: receipt.hash,
        error: ''
      });
    } catch (error) {
      setModalProps({
        title: 'Transaction Failed',
        message: 'There was an error processing your transaction.',
        isLoading: false,
        txHash: '',
        error: error.message,
      });
    }
  };

  const buyWithU = async() => {
      try {
        setModalOpen(true);
        setModalProps({
         title: 'Approving...',
         message: 'Please wait while your USDT approval transaction is being processed.',
         isLoading: true,
         txHash: '',
         error: ''
        });

        const amount = parseEther(buyAmount);
        const result = await usdtContract.approve(vendorAddress, amount);

        await result.wait();

        // After approval, update modal to Purchasing
      setModalProps({
        title: 'Purchasing...',
        message: 'Please wait while your purchase transaction is processed.',
        isLoading: true,
        txHash: '',
        error: ''
     });

        const tx = await vendorContract.buyWithUSDT(amount);
        const receipt = await tx.wait();
        
        // On success, update modal to show success and transaction hash
        setModalProps({
          title: 'Transaction Successful',
          message: 'Your transaction has been completed successfully.',
          isLoading: false,
          txHash: receipt.hash,
          error: ''
       });

      } catch (error) {
        // On error, update modal to show error message
      setModalProps({
        title: 'Transaction Failed',
        message: 'There was an error processing your transaction.',
        isLoading: false,
        txHash: '',
        error: error.message,
     });
      }
  }

  return (
    <div className="presale-box">
      <div className="meteor"></div>
      <div className="meteor"></div>
      <div className="meteor"></div>
      <div className="meteor"></div>
      <div className="star"></div>
      <div className="star"></div>
      <div className="star"></div>
      <div className="star"></div>
      <div className="star"></div>
      <div className="star"></div>
      <h2 className="presale-title">Token Presale</h2>

      {/* Countdown */}
      {countdownTimestamp != 0 ? (
        <div className="countdown">
        <span>{timeLeft.days || 0}d </span>
        <span>{timeLeft.hours || 0}h </span>
        <span>{timeLeft.minutes || 0}m </span>
        <span>{timeLeft.seconds || 0}s</span>
      </div>
    ) : "Presale not started!"}

      {/* Progress Bar */}
      <div className="progress-container">
        <div
          className="progress-bar"
          style={{ width: `${soldPercentage}%` }}
        ></div>
        <span className="progress-text">{soldPercentage.toFixed(0)}% Sold</span>
      </div>

      <div className="token-info">
        <div>Tokens Sold: {Number(soldAmount).toFixed(2)}</div>
        <div>Tokens Available: {Number(totalAmount - soldAmount).toFixed(2)}</div>
      </div>

      {/* Token Price */}
      <div className="token-price">
        Price: ${usdPrice}
      </div>

      {/* Payment Icons (Selectable) */}
      <div className="payment-icons">
        {paymentOptions.map((option) => (
          <div
            key={option.name}
            className={`payment-icon ${selectedPayment === option.name ? 'selected' : ''}`}
            onClick={() => setSelectedPayment(option.name)}
          >
            <img src={option.icon} alt={option.name} />
            <span>{option.name}</span>
          </div>
        ))}
      </div>

      {/* Inputs Row or Column (responsive) */}
      <div className="input-row">
        <input
          type="number"
          className="presale-input"
          placeholder="Amount to Buy"
          value={buyAmount}
          onChange={(e) => setBuyAmount(e.target.value)}
        />
        <input
          type="text"
          className="presale-input"
          placeholder="You Receive"
          value={receiveAmount}
          readOnly
        />
      </div>

      {/* Buy Now Button */}
      {selectedPayment === 'BNB' ? (<button onClick={buyWithB} className="buy-button" disabled={!isConnected}>Buy Now</button>) : (
        <button onClick={buyWithU} className="buy-button" disabled={!isConnected}>Buy Now</button>
      )}

      {/* Reusable Modal Component */}
      <Modal
        isOpen={modalOpen}
        title={modalProps.title}
        message={modalProps.message}
        txHash={modalProps.txHash}
        error={modalProps.error}
        isLoading={modalProps.isLoading}
        onClose={closeModal}
      />

    </div>
  );
};

export default PresaleBox;
